import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Bookmark, BookmarkPlus, ExternalLink } from "lucide-react";
import { toast } from "sonner";

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "à l'instant";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}j`;
}

export default function BookmarksPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/bookmarks");
      setItems(data.items || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (item_id) => {
    await api.delete(`/bookmarks/${item_id}`);
    setItems((x) => x.filter((i) => i.item_id !== item_id));
    toast.success("Retiré des favoris");
  };

  return (
    <div className="p-6 lg:p-10 max-w-5xl">
      <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#FF453A] mb-3">
        Vos favoris
      </div>
      <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight mb-4" data-testid="bookmarks-title">
        Ce que vous voulez garder à portée.
      </h1>
      <p className="text-white/50 mb-10">
        Sauvegardez les articles, analyses d'experts et updates que vous souhaitez relire plus tard.
      </p>

      {loading ? (
        <div className="text-white/40 font-mono text-sm">Chargement…</div>
      ) : items.length === 0 ? (
        <div className="border border-white/10 bg-[#121215] p-12 text-center">
          <BookmarkPlus className="w-10 h-10 text-white/30 mx-auto mb-4" strokeWidth={1.5} />
          <div className="font-display text-2xl font-bold mb-2">Aucun favori pour le moment.</div>
          <p className="text-white/50">Cliquez sur l'icône <Bookmark className="w-4 h-4 inline mx-1" /> sur n'importe quel article du feed pour le sauvegarder ici.</p>
        </div>
      ) : (
        <div className="border-t border-l border-white/10">
          {items.map((it) => (
            <div key={it.item_id} className="p-5 border-r border-b border-white/10 hover:bg-white/[0.03] transition-colors group" data-testid={`bookmark-row-${it.item_id}`}>
              <div className="flex items-center gap-4 mb-2 text-[10px] font-mono uppercase tracking-[0.2em]">
                <span className="text-[#FF453A]">{it.source}</span>
                <span className="text-white/30">·</span>
                <span className="text-white/40">{it.source_type}</span>
                <span className="text-white/30">·</span>
                <span className="text-white/40">{timeAgo(it.published_at)}</span>
                <button
                  onClick={() => remove(it.item_id)}
                  data-testid={`bookmark-remove-${it.item_id}`}
                  className="ml-auto text-[#FF453A] hover:text-white"
                  title="Retirer des favoris"
                >
                  <Bookmark className="w-4 h-4 fill-current" />
                </button>
              </div>
              <a href={it.url} target="_blank" rel="noopener noreferrer" className="block">
                <h4 className="font-display text-lg font-semibold leading-snug mb-1 group-hover:text-[#FFD60A] transition-colors line-clamp-2 flex items-start gap-2">
                  <span className="flex-1">{it.title}</span>
                  <ExternalLink className="w-4 h-4 text-white/30 shrink-0 mt-1" />
                </h4>
                {it.summary && <p className="text-sm text-white/55 line-clamp-2">{it.summary}</p>}
              </a>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
